import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './lib/auth/AuthContext';
import { ThemeProvider } from './lib/theme';
import { ToastProvider } from './components/ui/Toaster';
import { RequireAuth, RoleRoute, RootRedirect } from './router/guards';
import { AppShell } from './components/layout/AppShell';
import { SignInScreen } from './features/auth/SignInScreen';
import { RegisterScreen } from './features/auth/RegisterScreen';
import { VerifyEmailScreen } from './features/auth/VerifyEmailScreen';
import { OnboardingScreen } from './features/auth/OnboardingScreen';
import { LoadingBlock } from './components/ui/States';

const DashboardScreen = lazy(() =>
  import('./features/dashboard/DashboardScreen').then((m) => ({ default: m.DashboardScreen })),
);
const ProfileScreen = lazy(() =>
  import('./features/profile/ProfileScreen').then((m) => ({ default: m.ProfileScreen })),
);
const SkripsiListScreen = lazy(() =>
  import('./features/skripsi/SkripsiListScreen').then((m) => ({ default: m.SkripsiListScreen })),
);
const SkripsiDetailScreen = lazy(() =>
  import('./features/skripsi/SkripsiDetailScreen').then((m) => ({
    default: m.SkripsiDetailScreen,
  })),
);
const BimbinganScreen = lazy(() =>
  import('./features/bimbingan/BimbinganScreen').then((m) => ({ default: m.BimbinganScreen })),
);
const ConsultScreen = lazy(() =>
  import('./features/consult/ConsultScreen').then((m) => ({ default: m.ConsultScreen })),
);
const ProdiRecapScreen = lazy(() =>
  import('./features/consult/ProdiRecapScreen').then((m) => ({ default: m.ProdiRecapScreen })),
);
const PengajuanReviewScreen = lazy(() =>
  import('./features/persyaratan/PengajuanReviewScreen').then((m) => ({
    default: m.PengajuanReviewScreen,
  })),
);
const PersyaratanManagerScreen = lazy(() =>
  import('./features/persyaratan/PersyaratanManagerScreen').then((m) => ({
    default: m.PersyaratanManagerScreen,
  })),
);

const NotificationsScreen = lazy(() =>
  import('./features/notifications/NotificationsScreen').then((m) => ({
    default: m.NotificationsScreen,
  })),
);
const DosenHistoryScreen = lazy(() =>
  import('./features/profile/DosenHistoryScreen').then((m) => ({ default: m.DosenHistoryScreen })),
);
const NewSkripsiScreen = lazy(() =>
  import('./features/skripsi/NewSkripsiScreen').then((m) => ({ default: m.NewSkripsiScreen })),
);
const DocumentsScreen = lazy(() =>
  import('./features/documents/DocumentsScreen').then((m) => ({ default: m.DocumentsScreen })),
);
const EventsScreen = lazy(() =>
  import('./features/events/EventsScreen').then((m) => ({ default: m.EventsScreen })),
);
const VerifikasiScreen = lazy(() =>
  import('./features/staff/VerifikasiScreen').then((m) => ({ default: m.VerifikasiScreen })),
);
const DataScreen = lazy(() =>
  import('./features/staff/DataScreen').then((m) => ({ default: m.DataScreen })),
);
const LaporanScreen = lazy(() =>
  import('./features/staff/LaporanScreen').then((m) => ({ default: m.LaporanScreen })),
);
const UsersScreen = lazy(() =>
  import('./features/admin/UsersScreen').then((m) => ({ default: m.UsersScreen })),
);
const SettingsScreen = lazy(() =>
  import('./features/admin/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
);
const JurusanScreen = lazy(() =>
  import('./features/admin/JurusanScreen').then((m) => ({ default: m.JurusanScreen })),
);
const SeedScreen = lazy(() =>
  import('./features/admin/SeedScreen').then((m) => ({ default: m.SeedScreen })),
);

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <Suspense
              fallback={
                <div className="mx-auto w-full max-w-[1080px] px-4 pt-6 md:px-8">
                  <LoadingBlock rows={3} />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/login" element={<SignInScreen />} />
                <Route path="/register" element={<RegisterScreen />} />
                <Route path="/verify-email" element={<VerifyEmailScreen />} />
                <Route path="/onboarding" element={<OnboardingScreen />} />

                <Route element={<RequireAuth />}>
                  <Route path="/app" element={<AppShell />}>
                    <Route path=":role" element={<RoleRoute />}>
                      <Route index element={<DashboardScreen />} />
                      <Route path="notifikasi" element={<NotificationsScreen />} />
                      <Route path="profil" element={<ProfileScreen />} />
                      <Route path="riwayat" element={<DosenHistoryScreen />} />
                      {/* Skripsi flow */}
                      <Route path="skripsi" element={<SkripsiListScreen />} />
                      <Route path="skripsi/baru" element={<NewSkripsiScreen />} />
                      <Route path="skripsi/:id" element={<SkripsiDetailScreen />} />
                      <Route path="skripsi/:id/dokumen" element={<DocumentsScreen />} />
                      {/* Guidance & events */}
                      <Route path="bimbingan" element={<BimbinganScreen />} />
                      <Route path="konsultasi" element={<ConsultScreen />} />
                      <Route path="seminar" element={<EventsScreen />} />
                      <Route path="jadwal" element={<EventsScreen />} />
                      <Route path="rekap" element={<ProdiRecapScreen />} />
                      {/* Pengajuan & persyaratan */}
                      <Route path="pengajuan" element={<PengajuanReviewScreen />} />
                      <Route path="persyaratan" element={<PersyaratanManagerScreen />} />
                      {/* Staff & admin */}
                      <Route path="verifikasi" element={<VerifikasiScreen />} />
                      <Route path="permintaan" element={<VerifikasiScreen />} />
                      <Route path="data" element={<DataScreen />} />
                      <Route path="laporan" element={<LaporanScreen />} />
                      <Route path="pengguna" element={<UsersScreen />} />
                      <Route path="organisasi" element={<JurusanScreen />} />
                      <Route path="pengaturan" element={<SettingsScreen />} />
                      <Route path="seed" element={<SeedScreen />} />
                    </Route>
                  </Route>
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
